import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from "@medsurf/flat-facades";
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { textExportFuncs } from '../../../workers/textExport.worker';
import * as FlatServices from '@medsurf/flat-services';
import dayjs from 'dayjs';

@Component({
    selector: 'medsurf-chapter-main-export',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './export.component.html',
    styleUrls: ['./export.component.scss'],
    standalone: false
})
export class ExportComponent {
  isUpdating = signal(false);
  node: FlatModels.NodeEntityModels.NodeEntityType | null;
  currentTextExportObjects: FlatModels.TextExportAuthorModels.TextExportObject[];
  textExportObjectCount = 0;
  text = signal('');
  textExcerpt = computed(() => {
    const limit = 2000;
    if (this.text().length < limit) return this.text();
    return this.text().substring(0, limit) + '...';
  })
  exportForm = new FormGroup(
    {
      displayType: new FormControl<FlatModels.TextExportAuthorModels.TextExportDisplayType>(FlatModels.TextExportAuthorModels.TextExportDisplayType.enum.TextStructured),
      htmlTags: new FormControl(false)
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.TextExportAuthorModels.TextExportSettingsSchema,
      ),
      updateOn: 'change',
    }
  );

  readonly FlatModels = FlatModels;

  constructor(private alertControlFacade: FlatFacades.AlertControlFacade,
              private nodeSharedFacade: FlatFacades.NodeSharedFacade,
              private mediaControlService: FlatServices.MediaControlService,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public textExportAuthorFacade: FlatFacades.TextExportAuthorFacade,
              public clipboardAuthorFacade: FlatFacades.ClipboardAuthorFacade) {
    // Update Node
    this.nodeSharedFacade.currentSelectedNode$.pipe(takeUntilDestroyed()).subscribe((node) => {
      this.node = node;
    });
    
    // Update Text Export Results
    this.textExportAuthorFacade.currentTextExportObjects$.pipe(takeUntilDestroyed()).subscribe((currentTextExportObjects) => {
      this.currentTextExportObjects = currentTextExportObjects;
      this.textExportObjectCount = this.currentTextExportObjects.length;
      
      if (this.textExportObjectCount > 0) {
        this.onSubmit();
      }
    });
  }

  onSubmit() {
    if (this.isUpdating()) {
      return;
    }
    this.isUpdating.set(true);
    
    const argObj: FlatModels.TextExportAuthorModels.TextExportArgType = {
      type: this.exportForm.value.displayType || FlatModels.TextExportAuthorModels.TextExportDisplayType.enum.TextStructured,
      keepHTMLTags: this.exportForm.value.htmlTags || false,
      textExports: this.currentTextExportObjects,
    };

    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('../../../workers/textExport.worker', import.meta.url));
      worker.onmessage = ({ data }) => {
        if (data instanceof Error) {
          this.isUpdating.set(false);
          return;
        }
        this.text.set(data);
        this.isUpdating.set(false);
      };
      worker.postMessage({ argObj });
    } else {
      this.text.set(textExportFuncs[argObj.type]?.(argObj));
      this.isUpdating.set(false);
    }
  }

  load() {
    if (!this.node) return;
    this.textExportAuthorFacade.requestGetTextExports(this.node).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  download(){
    // Get Filename
    let fileName = 'text_export';
    const title = this.nodeSharedFacade.snapshot_currentSelectedPage()?.title;
    if (title && title !== '') {
      fileName = title;
    }
    const date = dayjs().format('DD.MM.YYYY_HH.mm');
    fileName = `${fileName}_${date}`;

    const exportAsJson = this.exportForm.value.displayType === FlatModels.TextExportAuthorModels.TextExportDisplayType.enum.TextInJson 
    const blob = exportAsJson ? new Blob([this.text()], {type: 'application/json'}) : new Blob([this.text()], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);

    this.mediaControlService.downloadFile(url, `${fileName}.${exportAsJson ? 'json' : 'txt'}`);
  }
}
